import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  Activity, DollarSign, ShoppingBag, TrendingUp, Clock, AlertTriangle,
  Plus, Loader2, RefreshCw, Zap, ChevronRight, X, CheckCircle2, Package,
} from 'lucide-react';
import PaymentStatusPill from '../../../components/admin/PaymentStatusPill';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 8000;

const fmt = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  } catch { return '—'; }
};
const fmtTimeShort = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }); }
  catch { return ''; }
};

/* ===========================================================================
 * KPI tile — tablet-friendly chunky stat
 * ===========================================================================*/
const Kpi = ({ icon: Icon, label, value, sub, color = 'text-[#BA1826]', testId }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 flex items-start gap-3" data-testid={testId}>
    <div className={`w-10 h-10 rounded-lg bg-[#FEE2E2] flex items-center justify-center ${color} flex-shrink-0`}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-[#4B5563] font-bold">{label}</p>
      <p className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A] truncate">{value}</p>
      {sub && <p className="text-[11px] text-[#9CA3AF] mt-0.5">{sub}</p>}
    </div>
  </div>
);

/* ===========================================================================
 * Offline sale modal
 * ===========================================================================*/
const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'zelle', label: 'Zelle' },
  { value: 'cashapp', label: 'Cash App' },
  { value: 'venmo', label: 'Venmo' },
  { value: 'check', label: 'Check' },
  { value: 'other', label: 'Other' },
];

const OfflineSaleModal = ({ open, onClose, onSaved }) => {
  const [items, setItems] = useState([]);
  const [mode, setMode] = useState('inventory'); // inventory | custom
  const [itemId, setItemId] = useState('');
  const [qty, setQty] = useState(1);
  const [customName, setCustomName] = useState('');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [buyer, setBuyer] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    axios.get(`${BACKEND_URL}/api/admin/shop/items`, { withCredentials: true })
      .then(r => setItems((r.data || []).filter(i => i.enabled !== false)))
      .catch(() => setItems([]));
  }, [open]);

  if (!open) return null;

  const selectedItem = items.find(i => i.id === itemId);
  const remaining = selectedItem ? Math.max((selectedItem.inventory || 0) - (selectedItem.sold_qty || 0), 0) : null;
  const computedTotal = mode === 'inventory'
    ? (selectedItem ? (Number(selectedItem.price || 0) * Number(qty || 0)).toFixed(2) : '0.00')
    : (Number(amount || 0) || 0).toFixed(2);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setSubmitting(true);
    try {
      const payload = { payment_method: method, buyer_name: buyer, notes };
      if (mode === 'inventory') {
        if (!itemId) { setError('Choose an item'); setSubmitting(false); return; }
        payload.item_id = itemId; payload.quantity = Number(qty);
      } else {
        if (!customName.trim()) { setError('Enter a description'); setSubmitting(false); return; }
        if (!(Number(amount) > 0)) { setError('Enter a positive amount'); setSubmitting(false); return; }
        payload.custom_name = customName.trim(); payload.amount = Number(amount);
      }
      const r = await axios.post(`${BACKEND_URL}/api/admin/live-ops/offline-sale`, payload, { withCredentials: true });
      onSaved(r.data);
      // Reset
      setItemId(''); setQty(1); setCustomName(''); setAmount(''); setBuyer(''); setNotes('');
    } catch (e2) {
      setError(e2.response?.data?.detail || 'Could not record sale');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="offline-sale-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E7EB]">
          <h3 className="font-heading text-lg font-bold flex items-center gap-2"><Plus className="w-5 h-5 text-[#BA1826]" /> Record Offline Sale</h3>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#0A0A0A]" data-testid="offline-sale-close-btn"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="flex gap-2" data-testid="offline-sale-mode-toggle">
            {[['inventory', 'Inventory Item'], ['custom', 'Custom (non-inventory)']].map(([k, label]) => (
              <button key={k} type="button" onClick={() => setMode(k)}
                className={`flex-1 text-sm font-medium px-3 py-2 rounded border ${mode === k ? 'bg-[#BA1826] text-white border-[#BA1826]' : 'bg-white text-[#4B5563] border-[#E5E7EB] hover:border-[#BA1826]'}`}
                data-testid={`offline-mode-${k}`}>{label}</button>
            ))}
          </div>

          {mode === 'inventory' ? (
            <>
              <div>
                <label className="block text-xs font-bold text-[#4B5563] mb-1 uppercase">Item</label>
                <select value={itemId} onChange={e => setItemId(e.target.value)} required
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded text-sm" data-testid="offline-item-select">
                  <option value="">— Select an item —</option>
                  {items.map(i => {
                    const rem = (i.inventory || 0) - (i.sold_qty || 0);
                    return <option key={i.id} value={i.id}>{i.name} — {fmt(i.price)} ({i.inventory > 0 ? `${rem} left` : 'unlimited'})</option>;
                  })}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-[#4B5563] mb-1 uppercase">Quantity</label>
                <input type="number" min="1" max="100" value={qty} onChange={e => setQty(e.target.value)}
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded text-sm" data-testid="offline-qty-input" />
                {selectedItem && remaining !== null && Number(qty) > remaining && selectedItem.inventory > 0 && (
                  <p className="text-xs text-red-600 mt-1">Only {remaining} in stock</p>
                )}
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-xs font-bold text-[#4B5563] mb-1 uppercase">Description</label>
                <input type="text" value={customName} onChange={e => setCustomName(e.target.value)} required maxLength={80}
                  placeholder="e.g. Donation jar drop-in, Misc merch bundle"
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded text-sm" data-testid="offline-custom-name-input" />
              </div>
              <div>
                <label className="block text-xs font-bold text-[#4B5563] mb-1 uppercase">Amount ($)</label>
                <input type="number" step="0.01" min="0.01" value={amount} onChange={e => setAmount(e.target.value)} required
                  className="w-full px-3 py-2 border border-[#E5E7EB] rounded text-sm" data-testid="offline-amount-input" />
              </div>
            </>
          )}

          <div>
            <label className="block text-xs font-bold text-[#4B5563] mb-1 uppercase">Payment Method</label>
            <div className="grid grid-cols-3 gap-2" data-testid="offline-method-grid">
              {PAYMENT_METHODS.map(pm => (
                <button key={pm.value} type="button" onClick={() => setMethod(pm.value)}
                  className={`text-xs font-semibold py-2.5 rounded border ${method === pm.value ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]' : 'bg-white text-[#4B5563] border-[#E5E7EB] hover:border-[#0A0A0A]'}`}
                  data-testid={`offline-method-${pm.value}`}>{pm.label}</button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-[#4B5563] mb-1 uppercase">Buyer Name (optional)</label>
            <input type="text" value={buyer} onChange={e => setBuyer(e.target.value)} maxLength={80}
              placeholder="Walk-in customer" className="w-full px-3 py-2 border border-[#E5E7EB] rounded text-sm" data-testid="offline-buyer-input" />
          </div>

          <div>
            <label className="block text-xs font-bold text-[#4B5563] mb-1 uppercase">Notes (optional)</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} maxLength={500}
              className="w-full px-3 py-2 border border-[#E5E7EB] rounded text-sm resize-none" data-testid="offline-notes-input" />
          </div>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 p-2 rounded text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> {error}</div>}

          <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded p-3 flex items-center justify-between">
            <span className="text-sm font-bold uppercase tracking-wide text-[#4B5563]">Total</span>
            <span className="text-2xl font-bold font-mono text-[#BA1826]" data-testid="offline-sale-total">${computedTotal}</span>
          </div>

          <button type="submit" disabled={submitting} className="w-full btn-primary text-sm disabled:opacity-50" data-testid="offline-sale-submit-btn">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Recording...</> : <><CheckCircle2 className="w-4 h-4" /> Record Sale</>}
          </button>
        </form>
      </div>
    </div>
  );
};

/* ===========================================================================
 * Live Ops main tab
 * ===========================================================================*/
const AdminLiveOpsTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [offlineOpen, setOfflineOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const lastIdRef = useRef(null);

  const fetchDashboard = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/live-ops/dashboard`, { withCredentials: true });
      setData(r.data);
      // Toast on new sale: detect via top feed id change after first load
      const topId = r.data.feed?.[0]?.id;
      if (lastIdRef.current && topId && topId !== lastIdRef.current) {
        const newest = r.data.feed[0];
        setToast({ kind: 'new', message: `New ${newest.source === 'offline_sale' ? 'offline' : ''} sale: ${newest.item_summary} (${fmt(newest.total)})` });
      }
      lastIdRef.current = topId || lastIdRef.current;
      setError('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load Live Ops');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    const t = setInterval(fetchDashboard, POLL_MS);
    return () => clearInterval(t);
  }, [fetchDashboard]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const today = data?.today;
  const feed = data?.feed || [];
  const low = data?.low_stock || { urgent: [], warning: [], total_low: 0 };
  const funnel = data?.popup_funnel;
  const urgentCount = low.urgent.length;
  const warnCount = low.warning.length;

  const handleSaleSaved = (result) => {
    setOfflineOpen(false);
    setToast({ kind: 'saved', message: `Offline sale ${result.order_number} recorded · ${fmt(result.total)}` });
    fetchDashboard();
  };

  if (loading && !data) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#BA1826]" /></div>;
  }

  return (
    <div className="space-y-6 pb-20" data-testid="admin-live-ops-tab">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-bold text-[#0A0A0A] flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            Live Event Operations Mode
          </h2>
          <p className="text-sm text-[#4B5563] mt-1">
            Real-time fundraiser + merch dashboard. Auto-refreshes every {POLL_MS / 1000}s.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchDashboard} disabled={loading}
            className="text-xs px-3 py-2 border border-[#E5E7EB] rounded hover:bg-[#F8F9FA] flex items-center gap-1.5 disabled:opacity-50"
            data-testid="live-ops-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button onClick={() => setOfflineOpen(true)}
            className="text-sm bg-[#0A0A0A] text-white px-4 py-2 rounded hover:bg-[#333] flex items-center gap-1.5 font-medium"
            data-testid="live-ops-offline-sale-btn">
            <Plus className="w-4 h-4" /> Record Offline Sale
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded text-sm">{error}</div>}

      {/* Toast */}
      {toast && (
        <div className={`fixed top-20 right-6 z-40 max-w-sm px-4 py-3 rounded-lg shadow-lg border-2 ${
          toast.kind === 'saved' ? 'bg-green-600 border-green-400 text-white' : 'bg-blue-600 border-blue-400 text-white'
        } animate-in fade-in slide-in-from-top-2`} data-testid="live-ops-toast">
          <div className="flex items-center gap-2 font-semibold text-sm">
            <CheckCircle2 className="w-4 h-4" /> {toast.message}
          </div>
        </div>
      )}

      {/* TODAY METRICS — 5 KPI tiles */}
      <div>
        <h3 className="text-xs uppercase tracking-wider text-[#9CA3AF] font-bold mb-2">Today</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="today-metrics-row">
          <Kpi icon={DollarSign} label="Revenue Today" value={fmt(today?.revenue)} sub={today?.window_start ? new Date(today.window_start).toLocaleDateString() : ''} testId="kpi-today-revenue" />
          <Kpi icon={ShoppingBag} label="Orders Today" value={today?.orders ?? 0} sub={today?.orders ? 'all-status' : 'no orders yet'} testId="kpi-today-orders" />
          <Kpi icon={Package} label="Items Sold" value={today?.items_sold ?? 0} testId="kpi-today-items" />
          <Kpi icon={TrendingUp} label="Avg. Order Value" value={fmt(today?.average_order_value)} testId="kpi-today-aov" />
          <Kpi icon={Clock} label="Last Purchase" value={today?.last_purchase_at ? fmtTime(today.last_purchase_at) : '—'} sub={today?.last_purchase_at ? fmtTimeShort(today.last_purchase_at) : 'none today'} testId="kpi-today-last-purchase" />
        </div>
      </div>

      {/* LOW STOCK ALERTS */}
      {(urgentCount > 0 || warnCount > 0) && (
        <div className="space-y-3" data-testid="low-stock-alerts">
          {urgentCount > 0 && (
            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4" data-testid="low-stock-urgent-banner">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-red-700 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-bold text-red-900 text-sm">URGENT: {urgentCount} item{urgentCount === 1 ? '' : 's'} {urgentCount === 1 ? 'is' : 'are'} out or near-empty (&lt; {data?.thresholds?.urgent || 5})</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {low.urgent.map(i => (
                      <span key={i.id} className="text-xs font-mono bg-white border border-red-300 text-red-800 px-2 py-1 rounded">
                        {i.name}: <strong>{i.remaining === 0 ? 'OUT' : `${i.remaining} left`}</strong>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          {warnCount > 0 && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3" data-testid="low-stock-warning-banner">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-semibold text-amber-900 text-sm">Low stock: {warnCount} item{warnCount === 1 ? '' : 's'} below {data?.thresholds?.warning || 10}</p>
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    {low.warning.map(i => (
                      <span key={i.id} className="text-xs font-mono bg-white border border-amber-300 text-amber-800 px-2 py-0.5 rounded">
                        {i.name}: <strong>{i.remaining} left</strong>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* LIVE SALES FEED */}
        <div className="lg:col-span-2 bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="live-sales-feed">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold text-base flex items-center gap-2"><Activity className="w-4 h-4 text-[#BA1826]" /> Live Sales Feed</h3>
            <span className="text-[10px] text-[#9CA3AF]">last 6 hours · auto-refreshing</span>
          </div>
          {feed.length === 0 ? (
            <p className="text-sm text-[#9CA3AF] text-center py-10">No sales yet. Recorded offline sales and completed Stripe payments will appear here in real time.</p>
          ) : (
            <ul className="divide-y divide-[#F1F5F9]" data-testid="live-sales-feed-list">
              {feed.map((row) => (
                <li key={row.id} className="py-2.5 flex items-center gap-3" data-testid={`feed-row-${row.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-sm text-[#0A0A0A] truncate">{row.item_summary}</p>
                      <PaymentStatusPill status={row.status} testId={`feed-pill-${row.id}`} />
                      {row.source === 'offline_sale' && (
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-purple-100 text-purple-800 border border-purple-200 px-1.5 py-0.5 rounded" data-testid={`feed-offline-tag-${row.id}`}>
                          Offline · {row.payment_method}
                        </span>
                      )}
                      {row.source === 'pop_up_shop' && (
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-800 border border-blue-200 px-1.5 py-0.5 rounded">
                          Pop-Up
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-[#4B5563] truncate">
                      {row.first_name} · {row.order_number} · {fmtTime(row.created_at)}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-mono font-bold text-base">{fmt(row.total)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* POP-UP CONVERSION FUNNEL */}
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="popup-conversion-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold text-base flex items-center gap-2"><Zap className="w-4 h-4 text-[#BA1826]" /> Pop-Up Conversion</h3>
            <span className="text-[10px] text-[#9CA3AF]">last 24h</span>
          </div>
          {funnel && (
            <div className="space-y-2">
              {[
                ['QR scans', funnel.scans, 'kpi-scans'],
                ['Checkout opens', funnel.checkout_opens, 'kpi-opens'],
                ['Payments started', funnel.payments_started, 'kpi-starts'],
                ['Completed', funnel.completed, 'kpi-completed', 'text-green-700'],
                ['Failed', funnel.failed, 'kpi-failed', 'text-red-700'],
              ].map(([label, val, tid, color]) => (
                <div key={label} className="flex items-center justify-between py-1.5 border-b border-[#F1F5F9] last:border-0">
                  <span className="text-sm text-[#4B5563]">{label}</span>
                  <span className={`text-xl font-bold font-mono ${color || 'text-[#0A0A0A]'}`} data-testid={tid}>{val}</span>
                </div>
              ))}
              <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded p-3 mt-3 text-center">
                <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-bold">Scan → Purchase</p>
                <p className="text-3xl font-heading font-bold text-[#BA1826] mt-0.5" data-testid="kpi-conversion-rate">
                  {funnel.scan_to_purchase_rate_pct}%
                </p>
                <p className="text-[10px] text-[#9CA3AF] mt-1">{funnel.unique_visitors} unique visitor{funnel.unique_visitors === 1 ? '' : 's'}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <OfflineSaleModal open={offlineOpen} onClose={() => setOfflineOpen(false)} onSaved={handleSaleSaved} />

      {/* Mobile sticky CTA — visible only on small screens */}
      <button onClick={() => setOfflineOpen(true)}
        className="fixed lg:hidden bottom-6 right-6 z-30 bg-[#BA1826] text-white rounded-full px-5 py-3.5 shadow-2xl flex items-center gap-2 font-bold text-sm"
        data-testid="live-ops-fab-offline-sale">
        <Plus className="w-5 h-5" /> Offline Sale
      </button>
    </div>
  );
};

export default AdminLiveOpsTab;
